"use client";

import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Empty, Spin, Alert, Image } from "antd";
import { useTranslation } from "react-i18next";

import { useMessaging } from "../../context/MessagingContext.jsx";
import { fetchDoctors } from "../../../authDoctor/redux/thunks/doctorsThunks.js";
import { fetchUsers } from "../../../Profile/redux/thunks/userThunks.js";

import useRendezvous from "../../../appointments/hooks/useRendezvous";
import { axiosGateway } from "../../../api/axios";
import { MESSAGES_PATHS } from "../../../api/endpoints";
import { useSocket } from "../../context/SocketContext";

import "./ContactsList.css";
import { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation.js";

const DEFAULT_AVATAR =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

/* =========================================================
 * SMALL UTILS
 * ======================================================= */

const pick = (...vals) =>
  vals.find((v) => {
    if (v == null) return false;

    const s = String(v).trim();

    return (
      s.length > 0 &&
      s.toLowerCase() !== "undefined" &&
      s.toLowerCase() !== "null"
    );
  });

const cleanId = (id) => {
  if (id == null) return "";

  let s = String(id).trim();

  if (!s) return "";

  s = s.replace(/^DR_/i, "");
  s = s.replace(/^PAT_/i, "");
  s = s.trim();

  if (
    !s ||
    s.toLowerCase() === "nan" ||
    s.toLowerCase() === "undefined" ||
    s.toLowerCase() === "null"
  ) {
    return "";
  }

  return s;
};

const pickDocAnyId = (doc) =>
  doc?.externalId ??
  doc?.doctorId ??
  doc?.medecinId ??
  doc?.id ??
  doc?._id ??
  null;

/*
 * IMPORTANT:
 *
 * A doctor must always contact the PRINCIPAL patient.
 *
 * Backend /rendezvous/my-patients returns:
 *
 * patientId
 * subprofileId
 * effectiveProfileId
 * profileScope
 *
 * Therefore patientId has priority over id when
 * the contact comes from a subprofile appointment.
 */
const pickPrincipalPatientId = (patient) =>
  cleanId(
    patient?.patientId ??
      patient?.principalUserId ??
      patient?.principalPatientId ??
      patient?.mainPatientId ??
      patient?.parentPatientId ??
      patient?.parentUserId ??
      patient?.userId ??
      patient?.externalId ??
      patient?.id ??
      patient?._id
  );

const isSubprofilePatient = (patient) => {
  if (!patient) return false;

  return Boolean(
    patient?.subprofileId ||
      patient?.subProfileId ||
      patient?.profileScope === "subprofile" ||
      patient?.source === "subprofile"
  );
};

const safeKeyId = (item) => {
  const raw =
    item?.id ??
    item?._id ??
    item?.userId ??
    item?.patientId ??
    item?.doctorId ??
    "";

  const cleaned = cleanId(raw);

  return (
    cleaned ||
    `row-${Math.random()
      .toString(16)
      .slice(2)}`
  );
};

const getMessageTimestamp = (msg) => {
  const raw =
    msg?.createdAt ||
    msg?.created_at ||
    msg?.timestamp ||
    msg?.sentAt ||
    msg?.updatedAt ||
    msg?.updated_at ||
    null;

  if (!raw) return 0;

  if (typeof raw === "number") {
    return raw;
  }

  const ts = new Date(raw).getTime();

  return Number.isNaN(ts) ? 0 : ts;
};

const getMessageSenderId = (msg) =>
  msg?.senderId ??
  msg?.fromUserId ??
  msg?.fromId ??
  msg?.from?.id ??
  msg?.from?._id ??
  msg?.from ??
  msg?.sender?.id ??
  msg?.sender?._id ??
  null;

const getMessageReceiverId = (msg) =>
  msg?.receiverId ??
  msg?.toUserId ??
  msg?.toId ??
  msg?.to?.id ??
  msg?.to?._id ??
  msg?.to ??
  msg?.receiver?.id ??
  msg?.receiver?._id ??
  null;

const formatContactDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toDateString() === new Date().toDateString()
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString();
};

/* =========================================================
 * CONTACT ROW
 * ======================================================= */

function ContactRow({
  contact,
  isSelected,
  onSelect,
  roleLabel,
  lastMessage,
  lastDate,
}) {
  const { t } = useTranslation();

  const name =
    contact.fullName ||
    contact.name ||
    `${contact.firstName || ""} ${
      contact.lastName || ""
    }`.trim() ||
    contact.email ||
    (roleLabel === "Doctor"
      ? t(
          "messaging.contacts.fallbackDoctorName"
        )
      : t(
          "messaging.contacts.fallbackPatientName"
        ));

  const rawAvatar = pick(
    contact.avatarUrl,
    contact.avatar,
    contact.photo
  );

  const avatarSrc = useMemo(() => {
    if (!rawAvatar) return undefined;

    return roleLabel === "Doctor"
      ? resolveDoctorAvatar(rawAvatar)
      : rawAvatar;
  }, [rawAvatar, roleLabel]);

  const getInitials = () => {
    const first =
      contact?.firstName?.[0] ||
      name.split(" ")[0]?.[0] ||
      "";

    const last =
      contact?.lastName?.[0] ||
      name
        .split(" ")
        .slice(1)
        .join(" ")[0] ||
      "";

    return (
      first + last
    ).toUpperCase() || "?";
  };

  const normalizeSpecialtyKey = (
    value
  ) => {
    if (!value) return null;

    const norm = value
      .toString()
      .trim()
      .toLowerCase();

    if (!norm) return null;

    if (norm.includes("dermatolog"))
      return "dermatology";

    if (norm.includes("pediatr"))
      return "pediatrics";

    if (norm.includes("cardiolog"))
      return "cardiology";

    if (norm.includes("neurolog"))
      return "neurology";

    if (norm.includes("psychiatr"))
      return "psychiatry";

    if (
      norm.includes("ophthalmolog") ||
      norm.includes("ophtalmolog")
    ) {
      return "ophthalmology";
    }

    if (norm.includes("orthop"))
      return "orthopedics";

    if (
      norm.includes("gynecolog") ||
      norm.includes("gynaecolog")
    ) {
      return "gynecology";
    }

    if (norm.includes("dentist"))
      return "dentistry";

    if (norm.includes("general"))
      return "general";

    if (
      norm === "ent" ||
      (norm.includes("ear") &&
        norm.includes("nose") &&
        norm.includes("throat"))
    ) {
      return "ent";
    }

    return null;
  };

  const specialty =
    contact.specialty ||
    contact.role ||
    "";

  const specialtyKey =
    normalizeSpecialtyKey(specialty);

  let displayRole;

  if (roleLabel === "Doctor") {
    if (specialtyKey) {
      displayRole = t(
        `doctorFinder.specialties.${specialtyKey}`
      );
    } else {
      displayRole =
        specialty ||
        t(
          "messaging.contacts.defaultDoctorSpecialty"
        );
    }
  } else {
    displayRole = t(
      "messaging.contacts.patientRole"
    );
  }

  return (
    <div
      className={`contact-row ${
        isSelected ? "is-active" : ""
      }`}
      onClick={() => onSelect(contact)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (
          e.key === "Enter" ||
          e.key === " "
        ) {
          e.preventDefault();
          onSelect(contact);
        }
      }}
    >
      <div className="cr-avatar">
        <div className="cr-avatar-img-wrap">
          {avatarSrc ? (
            <Image
              src={avatarSrc}
              width={48}
              height={48}
              preview={false}
              fallback={DEFAULT_AVATAR}
              className="cr-avatar-img"
              style={{
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              className="ch-avatar--placeholder"
              style={{
                background: "#DBEAFE",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: "700",
                fontSize: "18px",
                borderRadius: "50%",
                width: "48px",
                height: "48px",
              }}
            >
              {getInitials()}
            </div>
          )}
        </div>
      </div>

      <div className="cr-body">
        <div className="cr-top">
          <div className="cr-name">
            {name}
          </div>
        </div>

        <div className="cr-bottom">
          <div className={lastMessage ? "cr-preview" : "cr-role"}>
            {lastMessage || displayRole}
          </div>
        </div>
      </div>
      <div className="dcl-meta">
        {lastDate && <span className="dcl-time">{formatContactDate(lastDate)}</span>}
      </div>
    </div>
  );
}

/* =========================================================
 * MAIN LIST
 * ======================================================= */

export default function ContactsList({
  mode,
  onSelectContact,
  selectedContactId,
  additionalContacts = [],
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { subscribe } = useSocket() || {};
  const contactRefreshTimer = useRef(null);
  const [messageActivityByPeer, setMessageActivityByPeer] = useState({});

  const messaging = useMessaging();

  const {
    userRole,
    selectedContact: ctxSelected,
    selectContact: ctxSelect,
    lastActivityByPeerId,
    getPeerKey,
    currentUserId,
    principalUserId,
  } = messaging;

  /*
   * IMPORTANT:
   *
   * useRendezvous gives us the appointment-based
   * contact IDs.
   */
  const {
    getConfirmedDoctorIdsForPatient,
    getConfirmedPatientIdsForDoctor,
  } = useRendezvous();

  const effectiveMode =
    mode || userRole;

  const isDoctorMode =
    effectiveMode === "doctor";

  const loadMessageActivity = useCallback(async () => {
    try {
      const { data } = await axiosGateway.get(MESSAGES_PATHS.CONTACTS_ME, {
        withCredentials: true,
      });
      const contacts = Array.isArray(data) ? data : data?.data || [];
      const activity = {};

      contacts.forEach((entry) => {
        const peerId = cleanId(
          entry?.peerId ?? entry?.userId ?? entry?.patientId ?? entry?.fromUserId
        );
        if (!peerId) return;

        const date =
          entry?.lastDate ??
          entry?.lastMessageAt ??
          entry?.createdAt ??
          null;
        const preview =
          entry?.lastMessage ??
          entry?.lastMessagePreview ??
          entry?.message ??
          entry?.content ??
          "";
        const timestamp = date ? new Date(date).getTime() : 0;
        const previousTimestamp = activity[peerId]?.timestamp || 0;

        if (timestamp >= previousTimestamp) {
          activity[peerId] = {
            preview: String(preview || ""),
            date,
            timestamp: Number.isNaN(timestamp) ? 0 : timestamp,
          };
        }
      });

      setMessageActivityByPeer(activity);
    } catch (error) {
      console.error("[ContactsList] Failed to load last-message previews:", error);
    }
  }, []);

  useEffect(() => {
    loadMessageActivity();
  }, [loadMessageActivity]);

  useEffect(() => {
    if (!subscribe) return undefined;
    const refresh = () => {
      clearTimeout(contactRefreshTimer.current);
      contactRefreshTimer.current = setTimeout(loadMessageActivity, 300);
    };
    const unsubscribeNew = subscribe("newMessage", refresh);
    const unsubscribeNewEvent = subscribe("message:new", refresh);
    return () => {
      unsubscribeNew?.();
      unsubscribeNewEvent?.();
      clearTimeout(contactRefreshTimer.current);
    };
  }, [subscribe, loadMessageActivity]);

  /*
   * Context selection remains responsible for
   * opening the conversation.
   *
   * We do NOT navigate manually here.
   */
  const selectContact =
    onSelectContact ||
    ctxSelect ||
    (() => {});

  const effectiveSelectedIdRaw =
    selectedContactId ||
    ctxSelected?.meta?.registerId ||
    ctxSelected?.meta?.patientId ||
    ctxSelected?.meta?.doctorId ||
    ctxSelected?.id ||
    ctxSelected?.userId ||
    null;

  const effectiveSelectedId =
    cleanId(effectiveSelectedIdRaw);

  /* =========================================================
   * REDUX STATE
   * ======================================================= */

  const doctorState = useSelector(
    (state) => state.doctor
  );

  const doctors =
    doctorState?.doctors || [];

  const loadingDoctors =
    doctorState?.loading || false;

  const errorDoctors =
    doctorState?.error || null;

  const usersById = useSelector(
    (state) => state.user?.usersById
  );

  const loadingUsers = useSelector(
    (state) => state.user?.loading
  );

  const errorUsers = useSelector(
    (state) => state.user?.error
  );

  const patients = useMemo(
    () =>
      Object.values(
        usersById || {}
      ),
    [usersById]
  );

  /* =========================================================
   * APPOINTMENT CONTACT IDS
   * ======================================================= */

  const [
    confirmedDoctorIds,
    setConfirmedDoctorIds,
  ] = useState([]);

  const [
    confirmedPatientIds,
    setConfirmedPatientIds,
  ] = useState([]);

  const [
    appointmentsLoading,
    setAppointmentsLoading,
  ] = useState(false);

  const [
    appointmentsError,
    setAppointmentsError,
  ] = useState(null);

  /*
   * Load contacts from confirmed/rescheduled
   * appointments.
   */
  useEffect(() => {
    let cancelled = false;

    const loadAppointmentContacts =
      async () => {
        try {
          setAppointmentsLoading(true);
          setAppointmentsError(null);

          /*
           * ==========================================
           * DOCTOR
           * ==========================================
           *
           * Doctor gets patients having:
           * confirmed OR rescheduled appointments.
           *
           * The hook returns patient IDs.
           */
          if (isDoctorMode) {
            const ids =
              await getConfirmedPatientIdsForDoctor();

            if (!cancelled) {
              setConfirmedPatientIds(
                Array.isArray(ids)
                  ? ids
                  : []
              );
            }

            return;
          }

          /*
           * ==========================================
           * PATIENT
           * ==========================================
           *
           * Always use PRINCIPAL patient ID.
           *
           * If a subprofile is active, contacts still
           * belong to the main patient account.
           */
          const patientId = cleanId(
            principalUserId ||
              currentUserId
          );

          if (!patientId) {
            if (!cancelled) {
              setConfirmedDoctorIds([]);
            }

            return;
          }

          const ids =
            await getConfirmedDoctorIdsForPatient(
              patientId
            );

          if (!cancelled) {
            setConfirmedDoctorIds(
              Array.isArray(ids)
                ? ids
                : []
            );
          }
        } catch (error) {
          console.error(
            "[ContactsList] Failed to load appointment contacts:",
            error
          );

          if (!cancelled) {
            setAppointmentsError(
              error?.message ||
                "Unable to load contacts."
            );

            /*
             * Do not leave stale IDs after an error.
             */
            setConfirmedDoctorIds([]);
            setConfirmedPatientIds([]);
          }
        } finally {
          if (!cancelled) {
            setAppointmentsLoading(false);
          }
        }
      };

    loadAppointmentContacts();

    return () => {
      cancelled = true;
    };
  }, [
    isDoctorMode,
    principalUserId,
    currentUserId,
    getConfirmedDoctorIdsForPatient,
    getConfirmedPatientIdsForDoctor,
  ]);

  /* =========================================================
   * MESSAGES REDUX
   * ======================================================= */

  const allMessages = useSelector(
    (state) =>
      state.messages?.byChatId ||
      state.messages
        ?.messagesByConversation ||
      state.messages?.byConversation ||
      state.chat
        ?.messagesByConversation ||
      state.chat?.byConversation ||
      {}
  );

  /*
   * Map:
   *
   * peerId -> last message timestamp
   */
  const lastMsgTsByPeer = useMemo(() => {
    const map = {};

    const updateMap = (
      peerId,
      ts
    ) => {
      const key = cleanId(peerId);

      if (!key) return;

      if (
        !map[key] ||
        ts > map[key]
      ) {
        map[key] = ts;
      }
    };

    Object.values(
      allMessages || {}
    ).forEach((msgs) => {
      if (!Array.isArray(msgs))
        return;

      msgs.forEach((msg) => {
        const ts =
          getMessageTimestamp(msg);

        if (!ts) return;

        const senderId =
          getMessageSenderId(msg);

        const receiverId =
          getMessageReceiverId(msg);

        if (senderId) {
          updateMap(
            senderId,
            ts
          );
        }

        if (receiverId) {
          updateMap(
            receiverId,
            ts
          );
        }
      });
    });

    return map;
  }, [allMessages]);

  /* =========================================================
   * FETCH USERS / DOCTORS
   * ======================================================= */

  useEffect(() => {
    if (isDoctorMode) {
      dispatch(fetchUsers());
    }
  }, [
    dispatch,
    isDoctorMode,
  ]);

  /* =========================================================
   * LOADING / ERROR
   * ======================================================= */

  const listLoading = isDoctorMode
    ? Boolean(
        loadingUsers ||
          appointmentsLoading
      )
    : Boolean(
        loadingDoctors ||
          appointmentsLoading
      );

  const listError = isDoctorMode
    ? errorUsers ||
      appointmentsError
    : errorDoctors ||
      appointmentsError;

  /* =========================================================
   * BASE LIST
   * ======================================================= */

  const baseList = useMemo(() => {
    /*
     * ==========================================
     * DOCTOR MODE
     * ==========================================
     */

    if (isDoctorMode) {
      const sourcePatients =
        Array.isArray(patients)
          ? patients
          : [];

      const confirmedIds =
        new Set(
          confirmedPatientIds
            .map((id) =>
              cleanId(id)
            )
            .filter(Boolean)
        );

      const uniquePatients =
        new Map();

      sourcePatients.forEach(
        (patient) => {
          /*
           * If backend gives patientId,
           * ALWAYS prefer it over effective
           * subprofile id.
           */
          const principalId =
            pickPrincipalPatientId(
              patient
            );

          if (!principalId)
            return;

          /*
           * Only show patients who have
           * confirmed/rescheduled appointments.
           */
          if (
            !confirmedIds.has(
              principalId
            )
          ) {
            return;
          }

          /*
           * If we already have this principal
           * patient, do not create another row.
           */
          if (
            uniquePatients.has(
              principalId
            )
          ) {
            return;
          }

          uniquePatients.set(
            principalId,
            {
              ...patient,

              /*
               * Messaging must use principal
               * patient identity.
               */
              id: principalId,
              userId: principalId,
              patientId: principalId,
              principalPatientId:
                principalId,

              /*
               * Do not expose the subprofile as
               * a separate messaging identity.
               */
              subprofileId: null,
            }
          );
        }
      );

      const doctorContacts = additionalContacts.filter(
        (contact) =>
          String(contact?.meta?.role ?? contact?.role ?? "")
            .trim()
            .toLowerCase() === "doctor"
      );
      const existingIds = new Set(
        Array.from(uniquePatients.keys()).map((id) => cleanId(id))
      );

      doctorContacts.forEach((contact) => {
        const doctorId = cleanId(
          contact?.meta?.doctorId ?? contact?.id ?? contact?.userId
        );
        if (!doctorId || existingIds.has(doctorId)) return;
        existingIds.add(doctorId);
        uniquePatients.set(doctorId, {
          ...contact,
          id: doctorId,
          userId: doctorId,
          peerId: doctorId,
        });
      });

      return Array.from(uniquePatients.values());
    }

    /*
     * ==========================================
     * PATIENT MODE
     * ==========================================
     *
     * Only doctors with confirmed/rescheduled
     * appointments.
     */

    const confirmedIds =
      new Set(
        confirmedDoctorIds
          .map((id) =>
            cleanId(id)
          )
          .filter(Boolean)
      );

    return (
      Array.isArray(doctors)
        ? doctors
        : []
    ).filter((doctor) => {
      const doctorId =
        cleanId(
          pickDocAnyId(doctor)
        );

      return (
        doctorId &&
        confirmedIds.has(
          doctorId
        )
      );
    });
  }, [
    isDoctorMode,
    patients,
    doctors,
    confirmedPatientIds,
    confirmedDoctorIds,
    additionalContacts,
  ]);

  /* =========================================================
   * SEARCH
   * ======================================================= */

  const [search, setSearch] =
    useState("");

  /* =========================================================
   * LAST ACTIVITY
   * ======================================================= */

  const getTs = useCallback(
    (item) => {
      /*
       * For doctor contacts:
       * use principal patient ID.
       *
       * For patient contacts:
       * use doctor ID.
       */
      const rawId =
        isDoctorMode
          ? pickPrincipalPatientId(
              item
            )
          : item?.id ??
            item?._id ??
            item?.userId ??
            item?.doctorId ??
            item?.patientId ??
            item?.externalId ??
            item?.medecinId ??
            null;

      const contactId =
        cleanId(rawId);

      const tsFromMessages =
        contactId
          ? Number(
              lastMsgTsByPeer[
                contactId
              ] || 0
            )
          : 0;

      const key =
        getPeerKey?.(item);

      const tsFromContext =
        key
          ? Number(
              lastActivityByPeerId?.[
                String(key)
              ] || 0
            )
          : 0;

      const tsFromContact =
        contactId
          ? Number(messageActivityByPeer[contactId]?.timestamp || 0)
          : 0;

      return Math.max(
        tsFromMessages,
        tsFromContext,
        tsFromContact
      );
    },
    [
      isDoctorMode,
      lastMsgTsByPeer,
      messageActivityByPeer,
      lastActivityByPeerId,
      getPeerKey,
    ]
  );

  /* =========================================================
   * SORT
   * ======================================================= */

  const sortedList = useMemo(() => {
    const arr =
      baseList.map(
        (item, idx) => ({
          item,
          idx,
        })
      );

    arr.sort((A, B) => {
      const ta =
        getTs(A.item);

      const tb =
        getTs(B.item);

      const aHasActivity =
        ta > 0;

      const bHasActivity =
        tb > 0;

      if (
        aHasActivity !==
        bHasActivity
      ) {
        return (
          Number(bHasActivity) -
          Number(aHasActivity)
        );
      }

      if (
        aHasActivity &&
        bHasActivity &&
        tb !== ta
      ) {
        return tb - ta;
      }

      return A.idx - B.idx;
    });

    return arr.map(
      (x) => x.item
    );
  }, [
    baseList,
    getTs,
  ]);

  /* =========================================================
   * SEARCH FILTER
   * ======================================================= */

  const filteredList = useMemo(() => {
    const q =
      search
        .trim()
        .toLowerCase();

    if (!q)
      return sortedList;

    return sortedList.filter(
      (item) => {
        const fullName = (
          item.fullName ||
          item.name ||
          `${item.firstName || ""} ${
            item.lastName || ""
          }`
        )
          .trim()
          .toLowerCase();

        const firstName =
          (
            item.firstName ||
            ""
          ).toLowerCase();

        const lastName =
          (
            item.lastName ||
            ""
          ).toLowerCase();

        const spec =
          (
            item.specialty ||
            ""
          ).toLowerCase();

        const city =
          (
            item.city ||
            ""
          ).toLowerCase();

        const email =
          (
            item.email ||
            ""
          ).toLowerCase();

        return (
          fullName.includes(q) ||
          firstName.includes(q) ||
          lastName.includes(q) ||
          `${firstName} ${lastName}`.includes(
            q
          ) ||
          `${lastName} ${firstName}`.includes(
            q
          ) ||
          spec.includes(q) ||
          city.includes(q) ||
          email.includes(q)
        );
      }
    );
  }, [
    sortedList,
    search,
  ]);

  /* =========================================================
   * SELECT CONTACT
   *
   * IMPORTANT:
   *
   * NO navigate()
   *
   * MessagingContext owns the messaging route/layout.
   * This prevents a doctor from accidentally entering
   * the patient AppLayout.
   * ======================================================= */

  const handleSelect =
    useCallback(
      (item) => {
        if (!item) return;

        /*
         * ==========================================
         * PATIENT -> DOCTOR
         * ==========================================
         */

        if (!isDoctorMode) {
          const doctorId =
            cleanId(
              pickDocAnyId(item)
            );

          if (!doctorId) {
            console.error(
              "[ContactsList] Doctor ID not found:",
              item
            );

            return;
          }

          const contact = {
            id: doctorId,
            userId: doctorId,
            peerId: doctorId,

            name:
              item.fullName ||
              item.name,

            fullName:
              item.fullName ||
              item.name,

            avatar:
              item.avatarUrl ||
              item.photo ||
              item.avatar,

            email: item.email,
            specialty:
              item.specialty,
            phone: item.phone,
            city: item.city,

            meta: {
              type: "doctor",
              role: "doctor",

              registerId:
                doctorId,

              doctorId,

              email:
                item.email,

              specialty:
                item.specialty,

              displayName:
                item.fullName ||
                item.name,
            },
          };

          /*
           * IMPORTANT:
           * Let MessagingContext handle navigation
           * and preserve the patient layout/session.
           */
          selectContact(contact);

          return;
        }

        /*
         * ==========================================
         * DOCTOR -> PATIENT
         * ==========================================
         *
         * ALWAYS use the principal patient ID.
         */

        if (
          String(item?.meta?.role ?? item?.role ?? "")
            .trim()
            .toLowerCase() === "doctor"
        ) {
          selectContact(item);
          return;
        }

        const patientId =
          pickPrincipalPatientId(
            item
          );

        if (!patientId) {
          console.error(
            "[ContactsList] Patient ID not found:",
            item
          );

          return;
        }

        const fullName =
          item.fullName ||
          `${item.firstName || ""} ${
            item.lastName || ""
          }`.trim() ||
          item.name ||
          "Patient";

        const contact = {
          /*
           * IMPORTANT:
           * This is the PRINCIPAL patient ID,
           * not subprofile/effectiveProfileId.
           */
          id: patientId,
          userId: patientId,
          peerId: patientId,

          name: fullName,
          fullName,

          avatar:
            item.avatar ||
            item.avatarUrl ||
            item.photo,

          email:
            item.email,

          phone:
            item.phone,

          dateOfBirth:
            item.dateOfBirth,

          gender:
            item.gender,

          meta: {
            type: "patient",
            role: "patient",

            registerId:
              patientId,

            patientId,

            principalPatientId:
              patientId,

            email:
              item.email,

            displayName:
              fullName,

            /*
             * Never make the subprofile the
             * messaging identity.
             */
            subprofileId:
              null,
          },
        };

        /*
         * VERY IMPORTANT:
         *
         * Do not navigate manually.
         *
         * selectContact() keeps the doctor inside
         * the doctor messaging/layout context.
         */
        selectContact(contact);
      },
      [
        isDoctorMode,
        selectContact,
      ]
    );

  /* =========================================================
   * UI
   * ======================================================= */

  const isLoading =
    listLoading;

  const shouldEnableListScroll =
    filteredList.length > 1;

  return (
    <div className="contacts-container">
      <div className="messages-header">
        <div className="messages-title">
          {isDoctorMode
            ? t(
                "messaging.contacts.titlePatients"
              )
            : t(
                "messaging.contacts.titleDoctors"
              )}
        </div>

        <div className="messages-subtitle">
          {isDoctorMode
            ? t(
                "messaging.contacts.countPatients",
                {
                  count:
                    filteredList.length,
                }
              )
            : t(
                "messaging.contacts.countDoctors",
                {
                  count:
                    filteredList.length,
                }
              )}
        </div>
      </div>

      <div className="search-container">
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder={
              isDoctorMode
                ? t(
                    "messaging.contacts.searchPatientsPlaceholder"
                  )
                : t(
                    "messaging.contacts.searchDoctorsPlaceholder"
                  )
            }
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
            aria-label={
              isDoctorMode
                ? t(
                    "messaging.contacts.searchPatientsAria"
                  )
                : t(
                    "messaging.contacts.searchDoctorsAria"
                  )
            }
          />

          <svg
            className="search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            stroke="currentColor"
            fill="none"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />

            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
        </div>
      </div>

      <div
        className={`contacts-list ${
          shouldEnableListScroll
            ? "contacts-list--scrollable"
            : "contacts-list--fit"
        }`}
      >
        {isLoading && (
          <div
            style={{
              padding: 24,
              textAlign: "center",
            }}
          >
            <Spin size="large" />

            <p
              style={{
                marginTop: 12,
                color: "#666",
              }}
            >
              {isDoctorMode
                ? t(
                    "messaging.contacts.loadingPatients"
                  )
                : t(
                    "messaging.contacts.loadingDoctors"
                  )}
            </p>
          </div>
        )}

        {listError &&
          !isLoading && (
            <div
              style={{
                padding: 16,
              }}
            >
              <Alert
                message={t(
                  "messaging.contacts.errorLoading"
                )}
                description={
                  listError
                }
                type="error"
                showIcon
                action={
                  <button
                    onClick={() => {
                      if (
                        isDoctorMode
                      ) {
                        dispatch(
                          fetchUsers()
                        );
                      } else {
                        dispatch(
                          fetchDoctors()
                        );
                      }
                    }}
                    style={{
                      padding:
                        "4px 12px",
                      background:
                        "#ff4d4f",
                      color:
                        "white",
                      border: "none",
                      borderRadius:
                        4,
                      cursor:
                        "pointer",
                    }}
                  >
                    {t(
                      "messaging.contacts.retry"
                    )}
                  </button>
                }
              />
            </div>
          )}

        {!isLoading &&
          !listError &&
          filteredList.length ===
            0 && (
            <Empty
              description={
                search
                  ? isDoctorMode
                    ? t(
                        "messaging.contacts.noPatientsSearch",
                        {
                          query:
                            search,
                        }
                      )
                    : t(
                        "messaging.contacts.noDoctorsSearch",
                        {
                          query:
                            search,
                        }
                      )
                  : isDoctorMode
                  ? t(
                      "messaging.contacts.noPatients"
                    )
                  : t(
                      "messaging.contacts.noDoctors"
                    )
              }
              style={{
                marginTop: 48,
              }}
            />
          )}

        {!isLoading &&
          (!listError || additionalContacts.length > 0) &&
          filteredList.map(
            (item) => {
              /*
               * Doctor:
               * principal patient ID.
               *
               * Patient:
               * doctor ID.
               */
              const itemId =
                isDoctorMode
                  ? pickPrincipalPatientId(
                      item
                    )
                  : cleanId(
                      pickDocAnyId(
                        item
                      )
                    );
              const activity = itemId
                ? messageActivityByPeer[itemId]
                : null;

              return (
                <ContactRow
                  key={safeKeyId(
                    item
                  )}
                  contact={
                    item
                  }
                  isSelected={
                    itemId &&
                    effectiveSelectedId
                      ? String(
                          itemId
                        ) ===
                        String(
                          effectiveSelectedId
                        )
                      : false
                  }
                  onSelect={
                    handleSelect
                  }
                  roleLabel={
                    isDoctorMode &&
                    String(item?.meta?.role ?? item?.role ?? "")
                      .trim()
                      .toLowerCase() !== "doctor"
                      ? "Patient"
                      : "Doctor"
                  }
                  lastMessage={activity?.preview}
                  lastDate={activity?.date}
                />
              );
            }
          )}
      </div>
    </div>
  );
}